import React, { useState } from 'react'
import { LIBERTY_IMAGES } from '../assets/asset'
import TokenSelector from '../components/TokenSelector'
import { useNavigate } from 'react-router-dom'
import { useWalletUI } from '../hooks/useWalletUI'
import { useSelectedTokenBalance } from '../hooks/useSelectedTokenBalance'
import { useSwapQuote } from '../hooks/useSwapQuote'
import useApproval from '../hooks/useApproval'

function normalizeDecimalInput(value) {
    const cleaned = String(value || '').replace(/[^\d.]/g, '')
    const parts = cleaned.split('.')
    const rawInt = parts[0] || ''
    const normalizedInt = rawInt.replace(/^0+(?=\d)/, '') || '0'
    if (parts.length <= 1) return normalizedInt
    return `${normalizedInt}.${parts.slice(1).join('')}`
}

const SHIELD_TOKEN_IN_INIT = {
    symbol: 'USDC',
    chain: 'PulseChain',
    chainId: 369,
    address: '0x15d38573d2feeb82e7ad5187ab8c1d52810b1f07',
    decimals: 6,
    image: LIBERTY_IMAGES.USDC,
    chainBadge: LIBERTY_IMAGES.PULSECHAIN,
}

const SHIELD_TOKEN_OUT_INIT = {
    symbol: 'USDC',
    chain: 'Ethereum',
    chainId: 1,
    address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    decimals: 6,
    image: LIBERTY_IMAGES.USDC,
    chainBadge: LIBERTY_IMAGES.IC_RAILGUN_LOG,
    showRailgunSuffix: true,
}

const Shield = () => {
    const navigate = useNavigate()
    const [tokenSelectorOpen, setTokenSelectorOpen] = useState(false)
    const [tokenPickerKind, setTokenPickerKind] = useState('in')
    const [shieldTokenIn, setShieldTokenIn] = useState(SHIELD_TOKEN_IN_INIT)
    const [shieldTokenOut, setShieldTokenOut] = useState(SHIELD_TOKEN_OUT_INIT)
    const [shieldAmount, setShieldAmount] = useState('0')
    const { address, isConnected, openConnectModal } = useWalletUI()
    const { approveContract, isApproveLoading } = useApproval()
    const { balance: shieldInBalance, loading: shieldInBalanceLoading, refresh: refreshShieldBalance } = useSelectedTokenBalance(
        shieldTokenIn,
        address
    )
    const { receiveAmount, rateText, feeText, loading: quoteLoading } = useSwapQuote({
        payToken: shieldTokenIn,
        receiveToken: shieldTokenOut,
        payAmount: shieldAmount,
    })
    return (
        <>
            <main className="pt-16 md:pt-20 pb-16 md:pb-0">
                {/*$*/}
                {/*/$*/}
                <div className="min-h-[calc(100vh-64px)] md:min-h-[calc(100vh-72px)] flex items-center justify-center py-4 md:py-6">
                    <div className="w-full max-w-[600px] px-4 md:px-6">
                        <div className="md:hidden">
                            <div className="relative">
                                <div className="relative z-10">
                                    <div className="flex justify-between items-center text-center mb-4">
                                        <span className="hidden md:block text-2xl italic font-soloist">
                                            SHIELD
                                        </span>
                                        <span className="md:hidden text-2xl italic font-soloist">
                                            SHIELD
                                        </span>
                                        <div className="flex items-center bg-[#242429] rounded-full p-1 gap-1">
                                            <button onClick={() => navigate('/shield')} className="rounded-full transition-all duration-200 p-[1px]">
                                                <div
                                                    className="flex items-center gap-1 pl-[6px] pr-[10px] py-[2px] rounded-full transition-all duration-200 text-white font-semibold"
                                                    style={{
                                                        background:
                                                            "linear-gradient(265.63deg, rgb(210, 0, 88) -2.84%, rgb(221, 34, 61) 60%, rgb(245, 110, 0) 117.61%)"
                                                    }}
                                                >
                                                    <img
                                                        alt="Public"
                                                        loading="lazy"
                                                        width={16}
                                                        height={16}
                                                        decoding="async"
                                                        data-nimg={1}
                                                        className=""
                                                        src={LIBERTY_IMAGES.IC_SECURITY_SAFE}
                                                        style={{ color: "transparent" }}
                                                    />
                                                    <span className="font-medium">Shield</span>
                                                </div>
                                            </button>
                                            <button onClick={() => navigate('/unshield')} className="rounded-full transition-all duration-200 ">
                                                <div className="flex items-center gap-1 pl-[6px] pr-[10px] py-[2px] rounded-full transition-all duration-200 text-gray-400 bg-[#242429] hover:text-gray-300">
                                                    <img
                                                        alt="Private"
                                                        loading="lazy"
                                                        width={16}
                                                        height={16}
                                                        decoding="async"
                                                        data-nimg={1}
                                                        className=""
                                                        src={LIBERTY_IMAGES.IC_KEY}
                                                        style={{ color: "transparent" }}
                                                    />
                                                    <span className="font-medium">Unshield</span>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="relative space-y-2">
                                        <div className="TokenInput_root__O1LCP bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] rounded-xl p-4 w-full">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                    You shield
                                                </span>
                                                <div className="flex flex-row items-center">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            className="text-xs md:text-sm cursor-pointer bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)] hover:bg-[#333333] text-gray-300 px-2 py-0.5 rounded"
                                                            onClick={() => setShieldAmount(isConnected ? (shieldInBalance || '0') : '0')}
                                                        >
                                                            Max
                                                        </button>
                                                        <span className="text-gray-400 text-xs md:text-sm flex items-center">
                                                            Balance: {isConnected && shieldInBalanceLoading ? '...' : (isConnected ? shieldInBalance : '0')} {shieldTokenIn.symbol}
                                                            <span
                                                                className="ml-1.5 cursor-pointer hover:opacity-70"
                                                                title="Refresh balance"
                                                                onClick={refreshShieldBalance}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width={20}
                                                                    height={20}
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        fill="#ffffff"
                                                                        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                                                                        opacity=".4"
                                                                    />
                                                                    <path
                                                                        fill="#ffffff"
                                                                        d="M16.759 11.93a.741.741 0 00-.85.64c-.12.87-.51 1.65-1.11 2.25-1.38 1.38-3.52 1.52-5.07.45h.75c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-2.67c-.41 0-.75.34-.75.75v2.67c0 .41.34.75.75.75s.75-.34.75-.75v-.94c1 .81 2.21 1.24 3.43 1.24 1.4 0 2.8-.53 3.87-1.6.83-.83 1.36-1.9 1.53-3.1a.734.734 0 00-.63-.86zM7.34 12.08c.37 0 .69-.27.74-.64.12-.87.51-1.65 1.11-2.25 1.38-1.38 3.52-1.52 5.07-.45h-.75c-.41 0-.75.34-.75.75s.34.75.75.75h2.67c.41 0 .75-.34.75-.75V6.82c0-.41-.34-.75-.75-.75s-.75.34-.75.75v.94c-2.15-1.74-5.3-1.63-7.29.36-.83.83-1.36 1.9-1.53 3.1-.06.41.23.79.64.85.02.01.06.01.09.01z"
                                                                    />
                                                                </svg>
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <button
                                                        className="rounded-xl font-medium transition-all duration-300 cursor-pointer bg-[#242429] hover:bg-[#2d2d33] px-1 pl-2 py-2 text-sm    sm:text-base text-xs"
                                                        style={{
                                                            background:
                                                                "linear-gradient(90deg, rgb(255, 9, 99) 13.81%, rgb(237, 125, 4) 69.03%) text",
                                                            WebkitTextFillColor: "transparent"
                                                        }}
                                                    >
                                                        <span>Buy</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="relative flex-1">
                                                    <div className="relative w-full">
                                                        <span
                                                            className="absolute top-0 left-0 invisible whitespace-pre font-semibold"
                                                            style={{
                                                                fontSize: 24,
                                                                fontFamily: "inherit",
                                                                lineHeight: "1.2"
                                                            }}
                                                        />
                                                        <input
                                                            inputMode="decimal"
                                                            pattern="[0-9]*[.,]?[0-9]*"
                                                            placeholder={0}
                                                            className="bg-transparent outline-none w-full font-semibold  pr-4"
                                                            type="text"
                                                            value={shieldAmount}
                                                            onChange={(e) => setShieldAmount(normalizeDecimalInput(e.target.value))}
                                                            style={{ fontSize: 24 }}
                                                        />
                                                    </div>
                                                    <div className="text-sm text-gray-400 mt-1 flex items-center">
                                                        <div className="text-[#9091A0] text-xs gap-2">
                                                            <span className="mr-1" style={{ display: "none" }}>
                                                                ~$0
                                                            </span>
                                                            <span className="">Min: 10 - Max: 25,000</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div
                                                        onClick={() => {
                                                            setTokenPickerKind('in')
                                                            setTokenSelectorOpen(true)
                                                        }}
                                                        className="inline-flex flex-col items-center justify-between bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)] py-1.5 md:py-1 px-2 rounded-full cursor-pointer hover:bg-[#333333]
  font-medium whitespace-nowrap self-start undefined"
                                                    >
                                                        <div className="flex items-center gap-2 w-full">
                                                            <div className="flex items-center w-full">
                                                                <div className="relative mr-2">
                                                                    <img
                                                                        alt={shieldTokenIn.symbol}
                                                                        loading="lazy"
                                                                        width={32}
                                                                        height={32}
                                                                        decoding="async"
                                                                        data-nimg={1}
                                                                        className="border border-[#393941] rounded-full"
                                                                        src={shieldTokenIn.image}
                                                                        style={{ color: "transparent" }}
                                                                    />
                                                                    <div className="absolute -bottom-1 -right-1">
                                                                        <img
                                                                            alt={shieldTokenIn.chain}
                                                                            loading="lazy"
                                                                            width={16}
                                                                            height={16}
                                                                            decoding="async"
                                                                            data-nimg={1}
                                                                            className="border border-[#393941] rounded-full"
                                                                            src={shieldTokenIn.chainBadge}
                                                                            style={{ color: "transparent" }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="flex flex-col items-start md:mr-2">
                                                                    <span className="text-[#F7F7F8] font-medium text-base">
                                                                        {shieldTokenIn.symbol}
                                                                    </span>
                                                                    <span className="text-[#9091A0] text-xs md:block hidden">
                                                                        {shieldTokenIn.chain}
                                                                    </span>
                                                                </div>
                                                                <img
                                                                    alt="Chevron Down"
                                                                    loading="lazy"
                                                                    width={20}
                                                                    height={20}
                                                                    decoding="async"
                                                                    data-nimg={1}
                                                                    className="ml-auto flex-shrink-0"
                                                                    src={LIBERTY_IMAGES.IC_CHEVRON_DOWN}
                                                                    style={{ color: "transparent" }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-[-8px] z-10">
                                            <button disabled="">
                                                <div className="border border-[#41424B] p-[7px] rounded-xl transition-all duration-200 bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)]">
                                                    <img
                                                        alt="Swap"
                                                        loading="lazy"
                                                        width={24}
                                                        height={24}
                                                        decoding="async"
                                                        data-nimg={1}
                                                        src={LIBERTY_IMAGES.ARROW_DOWN}
                                                        style={{ color: "transparent" }}
                                                    />
                                                </div>
                                            </button>
                                        </div>
                                        <div className="TokenInput_root__O1LCP bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] rounded-xl p-4 w-full">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                    You receive
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="relative flex-1">
                                                    <div className="relative w-full">
                                                        <span
                                                            className="absolute top-0 left-0 invisible whitespace-pre font-semibold"
                                                            style={{
                                                                fontSize: 24,
                                                                fontFamily: "inherit",
                                                                lineHeight: "1.2"
                                                            }}
                                                        />
                                                        <input
                                                            inputMode="decimal"
                                                            pattern="[0-9]*[.,]?[0-9]*"
                                                            placeholder={0}
                                                            className={`bg-transparent outline-none w-full font-semibold pr-4 ${quoteLoading ? 'text-transparent bg-white/10 rounded-md animate-pulse' : ''}`}
                                                            type="text"
                                                            value={quoteLoading ? ' ' : receiveAmount}
                                                            readOnly
                                                            style={{ fontSize: 24 }}
                                                        />
                                                    </div>
                                                    <div className="text-sm text-gray-400 mt-1 flex items-center">
                                                        <div className="text-[#9091A0] text-xs gap-2">
                                                            <span className="mr-1" style={{ display: "none" }}>
                                                                ~$0
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div
                                                        onClick={() => {
                                                            setTokenPickerKind('out')
                                                            setTokenSelectorOpen(true)
                                                        }}
                                                        className="inline-flex flex-col items-center justify-between bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)] py-1.5 md:py-1 px-2 rounded-full cursor-pointer hover:bg-[#333333]
  font-medium whitespace-nowrap self-start undefined"
                                                    >
                                                        <div className="flex items-center gap-2 w-full">
                                                            <div className="flex items-center w-full">
                                                                <div className="relative mr-2">
                                                                    <img
                                                                        alt={shieldTokenOut.symbol}
                                                                        loading="lazy"
                                                                        width={32}
                                                                        height={32}
                                                                        decoding="async"
                                                                        data-nimg={1}
                                                                        className="border border-[#393941] rounded-full"
                                                                        src={shieldTokenOut.image}
                                                                        style={{ color: "transparent" }}
                                                                    />
                                                                    <div className="absolute -bottom-1 -right-1">
                                                                        <img
                                                                            alt={shieldTokenOut.chain}
                                                                            loading="lazy"
                                                                            width={16}
                                                                            height={16}
                                                                            decoding="async"
                                                                            data-nimg={1}
                                                                            className="border border-[#393941] rounded-full"
                                                                            src={shieldTokenOut.chainBadge}
                                                                            style={{ color: "transparent" }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="flex flex-col items-start md:mr-2">
                                                                    <span className="text-[#F7F7F8] font-medium text-base">
                                                                        {shieldTokenOut.symbol}
                                                                        {shieldTokenOut.showRailgunSuffix ? (
                                                                            <span className="text-[#F5A425]"> · RAILGUN</span>
                                                                        ) : null}
                                                                    </span>
                                                                    <span className="text-[#9091A0] text-xs md:block block">
                                                                        {shieldTokenOut.chain}
                                                                    </span>
                                                                </div>
                                                                <img
                                                                    alt="Chevron Down"
                                                                    loading="lazy"
                                                                    width={20}
                                                                    height={20}
                                                                    decoding="async"
                                                                    data-nimg={1}
                                                                    className="ml-auto flex-shrink-0"
                                                                    src={LIBERTY_IMAGES.IC_CHEVRON_DOWN}
                                                                    style={{ color: "transparent" }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-4 p-4 bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] rounded-xl space-y-2 text-sm">
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-1">
                                                <span className="text-[#F5A425] font-bold pt-0.5 antialiased text-sm">
                                                    Your Railgun Private 0zk Address
                                                </span>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <input
                                                placeholder="Enter your Railgun address from Railway wallet."
                                                className="w-full bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)] rounded-lg p-3 pr-12 text-white placeholder:text-gray-500 outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-0 transition-all duration-200 text-base"
                                                type="text"
                                                defaultValue=""
                                            />
                                        </div>
                                    </div>
                                    <div className="bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] rounded-xl p-4 space-y-3 text-sm mt-4">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">Rate</span>
                                            {quoteLoading ? (
                                                <span className="inline-block h-4 w-24 rounded bg-white/15 animate-pulse" />
                                            ) : (
                                                <span className="text-white">{rateText}</span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">Estimated time</span>
                                            <span className="text-white">2-3 min</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">Protocol fee</span>
                                            {quoteLoading ? (
                                                <span className="inline-block h-4 w-24 rounded bg-white/15 animate-pulse" />
                                            ) : (
                                                <span className="text-white">{feeText}</span>
                                            )}
                                        </div>
                                        <div className="border-t border-gray-100/5 my-4" />
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-2">
                                                <span className="text-white font-medium">Gasless Mode</span>
                                                <button className="relative">
                                                    <img
                                                        alt="Information"
                                                        className="w-4 h-4 cursor-pointer hover:opacity-80 transition-opacity"
                                                        src={LIBERTY_IMAGES.IC_FILL_INFORMATION}
                                                    />
                                                </button>
                                            </div>
                                            <button className="transition-opacity hover:opacity-80">
                                                <img
                                                    alt="Toggle gasless mode"
                                                    className="w-12 h-6"
                                                    src={LIBERTY_IMAGES.IC_TOGGLE}
                                                />
                                            </button>
                                        </div>
                                        <div className="text-gray-400 text-xs ml-0">
                                            No gas required. All gas fees are fully covered by LibertySwap.
                                        </div>
                                    </div>
                                    <div className="mt-6">
                                        <button
                                            className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 px-6 py-3 text-base w-full   "
                                            style={{ background: "var(--gradient-1)" }}
                                            type="button"
                                            onClick={() => {
                                                if (!isConnected) {
                                                    openConnectModal?.()
                                                    return
                                                }
                                                approveContract()
                                            }}
                                            disabled={isConnected && isApproveLoading}
                                        >
                                            {isConnected ? (isApproveLoading ? 'Processing...' : 'Shield') : 'Connect Wallet'}
                                        </button>
                                    </div>
                                    <div className="mt-4">
                                        <div className="flex items-center justify-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity duration-200">
                                            <img
                                                alt="Coffee"
                                                loading="lazy"
                                                width={20}
                                                height={20}
                                                decoding="async"
                                                data-nimg={1}
                                                src={LIBERTY_IMAGES.IC_HEART_CUM}
                                                style={{ color: "transparent" }}
                                            />
                                            <span className="text-[#F5A425] font-medium text-sm">
                                                Buy us a coffee
                                            </span>
                                            <img
                                                alt="Arrow"
                                                loading="lazy"
                                                width={21}
                                                height={20}
                                                decoding="async"
                                                data-nimg={1}
                                                src={LIBERTY_IMAGES.IC_ARROW_UP_RIGHT_1}
                                                style={{ color: "transparent" }}
                                            />
                                        </div>
                                    </div>
                                    <div className="hidden md:block bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] px-4 py-3 flex flex-col items-center justify-center mt-6 -mx-6 w-[calc(100%+3rem)]">
                                        <div className="text-center text-sm text-neutral-400">
                                            Smart Contract is currently in beta.{" "}
                                            <button className="text-[#F5A425] hover:text-amber-400 transition-colors underline">
                                                Learn more
                                            </button>
                                        </div>
                                    </div>
                                    <div className="md:hidden mt-2">
                                        <div className="text-center text-sm text-neutral-400">
                                            Smart Contract is currently in beta.{" "}
                                            <button className="text-[#F5A425] hover:text-amber-400 transition-colors underline">
                                                Learn more
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="absolute -top-4 -left-4 -right-4 h-full z-0"
                                    style={{
                                        backgroundImage: `url(${LIBERTY_IMAGES.SHIELD_GRADIENT_BG})`,
                                        backgroundSize: "100%",
                                        backgroundPosition: "center top",
                                        backgroundRepeat: "no-repeat",
                                        pointerEvents: "none"
                                    }}
                                />
                            </div>
                        </div>
                        <div className="hidden md:block">
                            <div
                                className="pt-4 md:pt-6 pr-4 md:pr-6 pl-4 md:pl-6 w-full max-w-[600px] overflow-hidden rounded-xl bg-[#18181B]"
                                style={{
                                    backgroundImage: `url(${LIBERTY_IMAGES.SHIELD_GRADIENT_BG})`,
                                    backgroundSize: "100%",
                                    backgroundPosition: "center top",
                                    backgroundRepeat: "no-repeat"
                                }}
                            >
                                <div className="flex justify-between items-center text-center mb-4">
                                    <span className="hidden md:block text-2xl italic font-soloist">
                                        SHIELD
                                    </span>
                                    <span className="md:hidden text-2xl italic font-soloist">
                                        SHIELD
                                    </span>
                                    <div className="flex items-center bg-[#242429] rounded-full p-1 gap-1">
                                        <button onClick={() => navigate('/shield')} className="rounded-full transition-all duration-200 p-[1px]">
                                            <div
                                                className="flex items-center gap-1 pl-[6px] pr-[10px] py-[2px] rounded-full transition-all duration-200 text-white font-semibold"
                                                style={{
                                                    background:
                                                        "linear-gradient(265.63deg, rgb(210, 0, 88) -2.84%, rgb(221, 34, 61) 60%, rgb(245, 110, 0) 117.61%)"
                                                }}
                                            >
                                                <img
                                                    alt="Public"
                                                    loading="lazy"
                                                    width={16}
                                                    height={16}
                                                    decoding="async"
                                                    data-nimg={1}
                                                    className=""
                                                    src={LIBERTY_IMAGES.IC_SECURITY_SAFE}
                                                    style={{ color: "transparent" }}
                                                />
                                                <span className="font-medium">Shield</span>
                                            </div>
                                        </button>
                                        <button onClick={() => navigate('/unshield')} className="rounded-full transition-all duration-200 ">
                                            <div className="flex items-center gap-1 pl-[6px] pr-[10px] py-[2px] rounded-full transition-all duration-200 text-gray-400 bg-[#242429] hover:text-gray-300">
                                                <img
                                                    alt="Private"
                                                    loading="lazy"
                                                    width={16}
                                                    height={16}
                                                    decoding="async"
                                                    data-nimg={1}
                                                    className=""
                                                    src={LIBERTY_IMAGES.IC_KEY}
                                                    style={{ color: "transparent" }}
                                                />
                                                <span className="font-medium">Unshield</span>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                                <div className="relative space-y-2">
                                    <div className="TokenInput_root__O1LCP bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] rounded-xl p-4 w-full">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                You shield
                                            </span>
                                            <div className="flex flex-row items-center">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        className="text-xs md:text-sm cursor-pointer bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)] hover:bg-[#333333] text-gray-300 px-2 py-0.5 rounded"
                                                        onClick={() => setShieldAmount(isConnected ? (shieldInBalance || '0') : '0')}
                                                    >
                                                        Max
                                                    </button>
                                                    <span className="text-gray-400 text-xs md:text-sm flex items-center">
                                                        Balance: {isConnected && shieldInBalanceLoading ? '...' : (isConnected ? shieldInBalance : '0')} {shieldTokenIn.symbol}
                                                        <span
                                                            className="ml-1.5 cursor-pointer hover:opacity-70"
                                                            title="Refresh balance"
                                                            onClick={refreshShieldBalance}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width={20}
                                                                height={20}
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                            >
                                                                <path
                                                                    fill="#ffffff"
                                                                    d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                                                                    opacity=".4"
                                                                />
                                                                <path
                                                                    fill="#ffffff"
                                                                    d="M16.759 11.93a.741.741 0 00-.85.64c-.12.87-.51 1.65-1.11 2.25-1.38 1.38-3.52 1.52-5.07.45h.75c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-2.67c-.41 0-.75.34-.75.75v2.67c0 .41.34.75.75.75s.75-.34.75-.75v-.94c1 .81 2.21 1.24 3.43 1.24 1.4 0 2.8-.53 3.87-1.6.83-.83 1.36-1.9 1.53-3.1a.734.734 0 00-.63-.86zM7.34 12.08c.37 0 .69-.27.74-.64.12-.87.51-1.65 1.11-2.25 1.38-1.38 3.52-1.52 5.07-.45h-.75c-.41 0-.75.34-.75.75s.34.75.75.75h2.67c.41 0 .75-.34.75-.75V6.82c0-.41-.34-.75-.75-.75s-.75.34-.75.75v.94c-2.15-1.74-5.3-1.63-7.29.36-.83.83-1.36 1.9-1.53 3.1-.06.41.23.79.64.85.02.01.06.01.09.01z"
                                                                />
                                                            </svg>
                                                        </span>
                                                    </span>
                                                </div>
                                                <button
                                                    className="rounded-xl font-medium transition-all duration-300 cursor-pointer bg-[#242429] hover:bg-[#2d2d33] px-1 pl-2 py-2 text-sm    sm:text-base text-xs"
                                                    style={{
                                                        background:
                                                            "linear-gradient(90deg, rgb(255, 9, 99) 13.81%, rgb(237, 125, 4) 69.03%) text",
                                                        WebkitTextFillColor: "transparent"
                                                    }}
                                                >
                                                    <span>Buy</span>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="relative flex-1">
                                                <div className="relative w-full">
                                                    <span
                                                        className="absolute top-0 left-0 invisible whitespace-pre font-semibold"
                                                        style={{
                                                            fontSize: 24,
                                                            fontFamily: "inherit",
                                                            lineHeight: "1.2"
                                                        }}
                                                    />
                                                    <input
                                                        inputMode="decimal"
                                                        pattern="[0-9]*[.,]?[0-9]*"
                                                        placeholder={0}
                                                        className="bg-transparent outline-none w-full font-semibold  pr-4"
                                                        type="text"
                                                        value={shieldAmount}
                                                        onChange={(e) => setShieldAmount(normalizeDecimalInput(e.target.value))}
                                                        style={{ fontSize: 24 }}
                                                    />
                                                </div>
                                                <div className="text-sm text-gray-400 mt-1 flex items-center">
                                                    <div className="text-[#9091A0] text-xs gap-2">
                                                        <span className="mr-1" style={{ display: "none" }}>
                                                            ~$0
                                                        </span>
                                                        <span className="">Min: 10 - Max: 25,000</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    onClick={() => {
                                                        setTokenPickerKind('in')
                                                        setTokenSelectorOpen(true)
                                                    }}
                                                    className="inline-flex flex-col items-center justify-between bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)] py-1.5 md:py-1 px-2 rounded-full cursor-pointer hover:bg-[#333333]
  font-medium whitespace-nowrap self-start undefined"
                                                >
                                                    <div className="flex items-center gap-2 w-full">
                                                        <div className="flex items-center w-full">
                                                            <div className="relative mr-2">
                                                                <img
                                                                    alt={shieldTokenIn.symbol}
                                                                    loading="lazy"
                                                                    width={32}
                                                                    height={32}
                                                                    decoding="async"
                                                                    data-nimg={1}
                                                                    className="border border-[#393941] rounded-full"
                                                                    src={shieldTokenIn.image}
                                                                    style={{ color: "transparent" }}
                                                                />
                                                                <div className="absolute -bottom-1 -right-1">
                                                                    <img
                                                                        alt={shieldTokenIn.chain}
                                                                        loading="lazy"
                                                                        width={16}
                                                                        height={16}
                                                                        decoding="async"
                                                                        data-nimg={1}
                                                                        className="border border-[#393941] rounded-full"
                                                                        src={shieldTokenIn.chainBadge}
                                                                        style={{ color: "transparent" }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col items-start md:mr-2">
                                                                <span className="text-[#F7F7F8] font-medium text-base">
                                                                    {shieldTokenIn.symbol}
                                                                </span>
                                                                <span className="text-[#9091A0] text-xs md:block hidden">
                                                                    {shieldTokenIn.chain}
                                                                </span>
                                                            </div>
                                                            <img
                                                                alt="Chevron Down"
                                                                loading="lazy"
                                                                width={20}
                                                                height={20}
                                                                decoding="async"
                                                                data-nimg={1}
                                                                className="ml-auto flex-shrink-0"
                                                                src={LIBERTY_IMAGES.IC_CHEVRON_DOWN}
                                                                style={{ color: "transparent" }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-[-8px] z-10">
                                        <button disabled="">
                                            <div className="border border-[#41424B] p-[7px] rounded-xl transition-all duration-200 bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)]">
                                                <img
                                                    alt="Swap"
                                                    loading="lazy"
                                                    width={24}
                                                    height={24}
                                                    decoding="async"
                                                    data-nimg={1}
                                                    src={LIBERTY_IMAGES.ARROW_DOWN}
                                                    style={{ color: "transparent" }}
                                                />
                                            </div>
                                        </button>
                                    </div>
                                    <div className="TokenInput_root__O1LCP bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] rounded-xl p-4 w-full">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-gray-400 pt-0.5 antialiased text-xs md:text-sm">
                                                You receive
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="relative flex-1">
                                                <div className="relative w-full">
                                                    <span
                                                        className="absolute top-0 left-0 invisible whitespace-pre font-semibold"
                                                        style={{
                                                            fontSize: 24,
                                                            fontFamily: "inherit",
                                                            lineHeight: "1.2"
                                                        }}
                                                    />
                                                    <input
                                                        inputMode="decimal"
                                                        pattern="[0-9]*[.,]?[0-9]*"
                                                        placeholder={0}
                                                        className={`bg-transparent outline-none w-full font-semibold pr-4 ${quoteLoading ? 'text-transparent bg-white/10 rounded-md animate-pulse' : ''}`}
                                                        type="text"
                                                        value={quoteLoading ? ' ' : receiveAmount}
                                                        readOnly
                                                        style={{ fontSize: 24 }}
                                                    />
                                                </div>
                                                <div className="text-sm text-gray-400 mt-1 flex items-center">
                                                    <div className="text-[#9091A0] text-xs gap-2">
                                                        <span className="mr-1" style={{ display: "none" }}>
                                                            ~$0
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    onClick={() => {
                                                        setTokenPickerKind('out')
                                                        setTokenSelectorOpen(true)
                                                    }}
                                                    className="inline-flex flex-col items-center justify-between bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)] py-1.5 md:py-1 px-2 rounded-full cursor-pointer hover:bg-[#333333]
  font-medium whitespace-nowrap self-start undefined"
                                                >
                                                    <div className="flex items-center gap-2 w-full">
                                                        <div className="flex items-center w-full">
                                                            <div className="relative mr-2">
                                                                <img
                                                                    alt={shieldTokenOut.symbol}
                                                                    loading="lazy"
                                                                    width={32}
                                                                    height={32}
                                                                    decoding="async"
                                                                    data-nimg={1}
                                                                    className="border border-[#393941] rounded-full"
                                                                    src={shieldTokenOut.image}
                                                                    style={{ color: "transparent" }}
                                                                />
                                                                <div className="absolute -bottom-1 -right-1">
                                                                    <img
                                                                        alt={shieldTokenOut.chain}
                                                                        loading="lazy"
                                                                        width={16}
                                                                        height={16}
                                                                        decoding="async"
                                                                        data-nimg={1}
                                                                        className="border border-[#393941] rounded-full"
                                                                        src={shieldTokenOut.chainBadge}
                                                                        style={{ color: "transparent" }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col items-start md:mr-2">
                                                                <span className="text-[#F7F7F8] font-medium text-base">
                                                                    {shieldTokenOut.symbol}
                                                                    {shieldTokenOut.showRailgunSuffix ? (
                                                                        <span className="text-[#F5A425]"> · RAILGUN</span>
                                                                    ) : null}
                                                                </span>
                                                                <span className="text-[#9091A0] text-xs md:block block">
                                                                    {shieldTokenOut.chain}
                                                                </span>
                                                            </div>
                                                            <img
                                                                alt="Chevron Down"
                                                                loading="lazy"
                                                                width={20}
                                                                height={20}
                                                                decoding="async"
                                                                data-nimg={1}
                                                                className="ml-auto flex-shrink-0"
                                                                src={LIBERTY_IMAGES.IC_CHEVRON_DOWN}
                                                                style={{ color: "transparent" }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 p-4 bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] rounded-xl space-y-2 text-sm">
                                    <div className="flex justify-between items-center mb-3">
                                        <div className="flex items-center gap-1">
                                            <span className="text-[#F5A425] font-bold pt-0.5 antialiased text-sm">
                                                Your Railgun Private 0zk Address
                                            </span>
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <input
                                            placeholder="Enter your Railgun address from Railway wallet."
                                            className="w-full bg-[var(--exchange-secondary-mobile)] md:bg-[var(--exchange-secondary-desktop)] rounded-lg p-3 pr-12 text-white placeholder:text-gray-500 outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-0 transition-all duration-200 text-base"
                                            type="text"
                                            defaultValue=""
                                        />
                                    </div>
                                </div>
                                <div className="bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] rounded-xl p-4 space-y-3 text-sm mt-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Rate</span>
                                        {quoteLoading ? (
                                            <span className="inline-block h-4 w-24 rounded bg-white/15 animate-pulse" />
                                        ) : (
                                            <span className="text-white">{rateText}</span>
                                        )}
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Estimated time</span>
                                        <span className="text-white">2-3 min</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Protocol fee</span>
                                        {quoteLoading ? (
                                            <span className="inline-block h-4 w-24 rounded bg-white/15 animate-pulse" />
                                        ) : (
                                            <span className="text-white">{feeText}</span>
                                        )}
                                    </div>
                                    <div className="border-t border-gray-100/5 my-4" />
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            <span className="text-white font-medium">Gasless Mode</span>
                                            <button className="relative">
                                                <img
                                                    alt="Information"
                                                    className="w-4 h-4 cursor-pointer hover:opacity-80 transition-opacity"
                                                    src={LIBERTY_IMAGES.IC_FILL_INFORMATION}
                                                />
                                            </button>
                                        </div>
                                        <button className="transition-opacity hover:opacity-80">
                                            <img
                                                alt="Toggle gasless mode"
                                                className="w-12 h-6"
                                                src={LIBERTY_IMAGES.IC_TOGGLE}
                                            />
                                        </button>
                                    </div>
                                    <div className="text-gray-400 text-xs ml-0">
                                        No gas required. All gas fees are fully covered by LibertySwap.
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <button
                                        className="rounded-xl font-medium transition-all duration-300 cursor-pointer text-white hover:opacity-90 px-6 py-3 text-base w-full   "
                                        style={{ background: "var(--gradient-1)" }}
                                        type="button"
                                        onClick={() => {
                                            if (!isConnected) {
                                                openConnectModal?.()
                                                return
                                            }
                                            approveContract()
                                        }}
                                        disabled={isConnected && isApproveLoading}
                                    >
                                        {isConnected ? (isApproveLoading ? 'Processing...' : 'Shield') : 'Connect Wallet'}
                                    </button>
                                </div>
                                <div className="mt-4">
                                    <div className="flex items-center justify-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity duration-200">
                                        <img
                                            alt="Coffee"
                                            loading="lazy"
                                            width={20}
                                            height={20}
                                            decoding="async"
                                            data-nimg={1}
                                            src={LIBERTY_IMAGES.IC_HEART_CUM}
                                            style={{ color: "transparent" }}
                                        />
                                        <span className="text-[#F5A425] font-medium text-sm">
                                            Buy us a coffee
                                        </span>
                                        <img
                                            alt="Arrow"
                                            loading="lazy"
                                            width={21}
                                            height={20}
                                            decoding="async"
                                            data-nimg={1}
                                            src={LIBERTY_IMAGES.IC_ARROW_UP_RIGHT_1}
                                            style={{ color: "transparent" }}
                                        />
                                    </div>
                                </div>
                                <div className="hidden md:block bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] px-4 py-3 flex flex-col items-center justify-center mt-6 -mx-6 w-[calc(100%+3rem)]">
                                    <div className="text-center text-sm text-neutral-400">
                                        Smart Contract is currently in beta.{" "}
                                        <button className="text-[#F5A425] hover:text-amber-400 transition-colors underline">
                                            Learn more
                                        </button>
                                    </div>
                                </div>
                                <div className="md:hidden mt-2">
                                    <div className="text-center text-sm text-neutral-400">
                                        Smart Contract is currently in beta.{" "}
                                        <button className="text-[#F5A425] hover:text-amber-400 transition-colors underline">
                                            Learn more
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="w-full flex-shrink-0 snap-center mt-6">
                            <div className="bg-[var(--exchange-bg-mobile)] md:bg-[var(--exchange-bg-desktop)] rounded-xl px-6 py-3 border border-white/[0.03] flex items-center justify-between">
                                <div className="flex flex-col">
                                    <p className="text-white font-medium text-base">
                                        Our Integrations Powered by...
                                    </p>
                                </div>
                                <div className="flex items-center">
                                    <img
                                        alt="partner"
                                        loading="lazy"
                                        width={125}
                                        height={40}
                                        decoding="async"
                                        data-nimg={1}
                                        className="object-contain"
                                        src={LIBERTY_IMAGES.ALL_PARTNER}
                                        style={{ color: "transparent" }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mt-8 text-center text-xs text-neutral-400">
                            For your security, transaction data is automatically deleted after 48
                            hours.
                            <br />
                            Need help? Please contact our{" "}
                            <a
                                href="https://x.com/LibertySwapFi"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-amber-500 hover:text-amber-400 underline"
                            >
                                Support Team 24/7
                            </a>
                        </div>
                    </div>
                </div>
            </main>
            <TokenSelector
                full={false}
                open={tokenSelectorOpen}
                listMode={tokenPickerKind === 'in' ? 'shield-top' : 'railgun'}
                onClose={() => setTokenSelectorOpen(false)}
                onSelect={(sel) => {
                    const next = { ...sel }
                    if (tokenPickerKind === 'in') {
                        setShieldTokenIn(next)
                    } else {
                        setShieldTokenOut({
                            ...next,
                            chainBadge: LIBERTY_IMAGES.IC_RAILGUN_LOG,
                            showRailgunSuffix: true,
                        })
                    }
                }}
            />
        </>
    )
}

export default Shield